import React, { useCallback } from 'react';
import { type Theme, useStyletron } from 'baseui';
import type { StyleObject } from 'styletron-react';
import { useIsAnnotationSelected } from '../../../atoms/tool-atoms';
import { TIMELINE_ROW_KIND } from '../../../constants/frame-timeline-v4';
import type { TimelineActionsT, TimelineRowDescriptorT } from '../../../types/frame-timeline-v4';
import { getSpanAtFrame } from '../../../utils/frame-timeline-v4';
import { AnnotationCell } from './annotation-cell';
import { AttributeCell } from './attribute-cell';
import { RelationshipCell } from './relationship-cell';
type TimelineCellPropsT = {
  row: TimelineRowDescriptorT;
  frame: number;
  cellWidth: number;
  timelineActions?: TimelineActionsT;
};
const getTrackStyle = (theme: Theme, selected: boolean): StyleObject => ({
  boxSizing: 'border-box',
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: selected ? theme.colors.backgroundSecondary : theme.colors.backgroundPrimary
});
const getNavigateButtonStyle = (theme: Theme, selected: boolean): StyleObject => ({
  display: 'block',
  border: 'none',
  padding: '0',
  margin: '0',
  cursor: 'pointer',
  ...getTrackStyle(theme, selected),
  ':hover': {
    backgroundColor: selected ? theme.colors.backgroundTertiary : theme.colors.backgroundSecondary
  }
});

/* The virtual grid re-renders its whole visible window on every scroll/state change, so the
 TanStack-recommended pattern is to memoize each item/cell component (https://tanstack.com/virtual/latest).
 With stable `row` props, memo then re-renders only the few cells that actually changed. */
export const TimelineCell = React.memo(function TimelineCell({
  row,
  frame,
  cellWidth,
  timelineActions
}: TimelineCellPropsT) {
  const [css, theme] = useStyletron();
  const isSelected = useIsAnnotationSelected(row.annotationId);
  const frameIndex = frame - 1;
  const isNavigable = frame > 0 && getSpanAtFrame(row.spans, frameIndex) === null;
  const handleNavigate = useCallback(() => {
    timelineActions?.onNavigateToFrame?.(frameIndex);
  }, [frameIndex, timelineActions]);
  if (isNavigable) {
    return <button type="button" data-frame={frameIndex} data-annotation-id={row.annotationId} className={css(getNavigateButtonStyle(theme, isSelected))} aria-label={`Go to frame ${frameIndex + 1}`} onClick={handleNavigate} />;
  }
  const renderContent = () => {
    switch (row.kind) {
      case TIMELINE_ROW_KIND.ANNOTATION:
        return <AnnotationCell row={row} frame={frame} timelineActions={timelineActions} isSelected={isSelected} cellWidth={cellWidth} />;
      case TIMELINE_ROW_KIND.ATTRIBUTE:
        return <AttributeCell row={row} frame={frame} timelineActions={timelineActions} isSelected={isSelected} cellWidth={cellWidth} />;
      case TIMELINE_ROW_KIND.RELATIONSHIP:
        return <RelationshipCell row={row} frame={frame} cellWidth={cellWidth} isSelected={isSelected} />;
      default:
        return null;
    }
  };
  return <div data-frame={frameIndex} data-annotation-id={row.annotationId} className={css(getTrackStyle(theme, isSelected))}>
      {renderContent()}
    </div>;
});